import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchPaginatedVideos, searchVideos, fetchSpecialtyCounts } from '../../services/videoApi/videoApi';
import { fetchExternalVideos } from '../../services/externalVideoApi/externalVideoApi';
import type { ExternalVideo } from '../../services/externalVideoApi/externalVideoApi.types';
import type { Video as ApiVideo, SpecialtyCount } from '../../services/videoApi/videoApi.types';
import {
  UNCLASSIFIED_SPECIALTY_KEY,
  UNCLASSIFIED_SPECIALTY_LABEL,
  ALL_SPECIALTIES_LABEL
} from '../../services/videoApi/videoApi.types';
import { loadSpecialtyFilter, saveSpecialtyFilter } from '../../utils/specialtyFilterStorage';
import { Video, VideoSource, SortOrder, Pagination, UseVideoListReturn } from './types';
import { useIsMobile } from '../../config/breakpoints';
import { FALLBACK_LABELS, EXTERNAL_UPLOADER_ID } from '../../config/constants';

// 診療科フィルタの候補。取得前（loading）と取得失敗（error）は「候補なし」だが、
// 記憶していた選択を外すのは取得に成功して候補に無いと分かったとき（ready）だけ
type SpecialtyCountsState =
  | { status: 'loading'; entries: SpecialtyCount[] }
  | { status: 'ready'; entries: SpecialtyCount[] }
  | { status: 'error'; entries: SpecialtyCount[] };

/**
 * APIから返されるビデオデータをコンポーネントで使用するVideo型に変換する
 * @param apiVideo APIから返されるビデオデータ
 * @returns コンポーネントで使用するVideo型のデータ
 */
const mapApiVideoToVideo = (apiVideo: ApiVideo): Video => {
  const visibilityValue = apiVideo.visibility === 'public' || apiVideo.visibility === 'private' || apiVideo.visibility === 'hospital_only'
    ? apiVideo.visibility
    : undefined;

  return {
    id: apiVideo.id,
    title: apiVideo.title,
    name: apiVideo.name,
    thumbnail_url: apiVideo.thumbnail_url,
    thumbnail: apiVideo.thumbnail,
    view_count: apiVideo.view_count,
    duration: apiVideo.duration,
    publish_date: apiVideo.publish_date,
    created_at: apiVideo.created_at,
    // ここで落とすとカードが手術日を出せない。VideoCardMobile は未設定時に
    // 当日へフォールバックするため、「手術より前に投稿された動画」に見える。
    surgery_date: apiVideo.surgery_date,
    published_at: apiVideo.published_at,
    visibility: visibilityValue,
    // 診療科未設定の動画はケバブメニューの公開系を disabled にするため必要（videoSpecialtyStatus.ts）。
    // null を明示することで「未設定」と「情報が渡っていない」を区別できるようにする。
    specialty: apiVideo.specialty ?? null,
    specialty_name: apiVideo.specialty?.name,
    // アップローダー情報がない場合はデフォルト値を設定
    uploader: apiVideo.uploader ? {
      id: apiVideo.uploader.id,
      name: apiVideo.uploader.name,
      icon_path: apiVideo.uploader.icon_path,
      hospital: apiVideo.uploader.hospital ? {
        id: apiVideo.uploader.hospital.id,
        name: apiVideo.uploader.hospital.name
      } : undefined
    } : {
      id: 'unknown',
      name: '不明なユーザー'
    },
    uploader_id: apiVideo.uploader_id || 'unknown',
    description: apiVideo.description,
    images: apiVideo.images,
    // 「資料 N」バッジ用。外部動画には資料機能が無いため mapExternalVideoToVideo 側では引き継がない
    materials: apiVideo.materials,
    conversion_status: apiVideo.conversion_status,
  };
};

/**
 * 外部動画を VideoList 用の共通型に変換する
 */
const mapExternalVideoToVideo = (ev: ExternalVideo): Video => {
  return {
    id: `ext_${ev.id}`,
    title: ev.title ?? FALLBACK_LABELS.UNTITLED,
    name: ev.title ?? FALLBACK_LABELS.UNTITLED,
    thumbnail_url: ev.thumbnail_url,
    uploader: {
      id: EXTERNAL_UPLOADER_ID,
      name: ev.uploader_name ?? FALLBACK_LABELS.ANONYMOUS,
    },
    uploader_id: EXTERNAL_UPLOADER_ID,
    created_at: ev.created_at,
    isExternal: true,
    external_id: ev.id,
    source_key: ev.source_key,
    hospital_name: ev.hospital_name || undefined,
    external_status: ev.status,
  };
};

// 外部動画も自院動画と同じ件数単位でページングし、無限スクロールで追加取得する
const EXTERNAL_PAGE_LIMIT = 12;

// 動画の並び替えに使う日時を取得する（公開日時が無ければ作成日時にフォールバック）
const getVideoSortTime = (video: Video): number => {
  const dateValue = video.publish_date ?? video.created_at;
  const time = dateValue ? new Date(dateValue).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
};

export const useVideoList = (): UseVideoListReturn => {
  // 動画一覧とページネーション情報の状態管理
  const [internalVideos, setInternalVideos] = useState<Video[]>([]);
  const [externalVideos, setExternalVideos] = useState<Video[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, totalPages: 1 });
  const [currentPage, setCurrentPage] = useState<number>(1);
  // 外部動画側のページング状態（offsetは「次に取得する位置」）
  const [externalPagination, setExternalPagination] = useState<{
    offset: number;
    hasMore: boolean;
    // 取得失敗直後の一時停止フラグ。hasMoreとは別で管理し、
    // 「データが尽きた」と「一時的に取得できなかった」を区別する。
    // タブ切り替えで再度trueになったタイミングで自動的にリトライされる。
    pausedByError: boolean;
  }>({
    offset: 0,
    hasMore: true,
    pausedByError: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isLoadingRef = useRef(false);
  const isExternalLoadingRef = useRef(false);
  const [videoSource, setVideoSource] = useState<VideoSource>('all');
  const [filterSortOrder, setFilterSortOrder] = useState<SortOrder>('DESC');
  // 診療科フィルタ。選択はブラウザに残し、次回も同じ科で開く
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<string>(() => loadSpecialtyFilter());
  // いま見える動画が1本以上ある診療科（＋未分類）。空の科はフィルタに並べない
  const [specialtyCounts, setSpecialtyCounts] = useState<SpecialtyCountsState>({ status: 'loading', entries: [] });
  // 一覧取得に単調増加のIDを振り、古い応答が新しい状態を上書きしないようにする
  // （フィルタ変更や検索が、進行中の取得と重なったときのため）
  const listRequestIdRef = useRef(0);
  const [procedure, setProcedure] = useState('');
  const [internalError, setInternalError] = useState(false);
  const [externalError, setExternalError] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const limit = 12; // 1ページあたりの件数
  // 公開日順で並べるためのソートパラメータ
  const sortBy = 'publish_date';
  const sortOrder = filterSortOrder;

  const isMobile = useIsMobile(); // スマホ表示判定

  // 外部動画のページ取得リクエストに単調増加するIDを振り、応答受信時に
  // まだ最新のリクエストかどうかを判定する（sortOrderを素早く切り替えた際、
  // 古いリクエストの応答が新しい状態を上書きするレースコンディションを防止）
  const externalRequestIdRef = useRef(0);

  // 外部動画を1ページ分取得し、末尾に追記する（offset=0の場合は置き換え）。
  // 多重発火はisExternalLoadingRefで常に防ぐ。sortOrder変更時は呼び出し側
  // （下記effect）が明示的にロックを解除してから呼ぶことで優先発行する
  const loadExternalPage = useCallback(async (offset: number, sortOrderParam: SortOrder) => {
    if (isExternalLoadingRef.current) return;
    isExternalLoadingRef.current = true;
    const requestId = ++externalRequestIdRef.current;
    try {
      setExternalError(false);
      const { videos, hasMore } = await fetchExternalVideos({
        limit: EXTERNAL_PAGE_LIMIT,
        offset,
        sortOrder: sortOrderParam
      });
      // 待機中により新しいリクエストが発行されていたら、この応答は古いので破棄する
      if (externalRequestIdRef.current !== requestId) return;
      const converted = videos.map(mapExternalVideoToVideo);
      setExternalVideos(prev => {
        if (offset === 0) return converted;
        // 追記時に既存と重複するIDを除外する（ページングカーソルが動く可能性があり、
        // 境界付近で同じ動画が2回返る場合にReactのkey重複を防ぐ）
        const existingIds = new Set(prev.map(v => v.id));
        return [...prev, ...converted.filter(v => !existingIds.has(v.id))];
      });
      // 次のoffsetは要求limitではなく実際の返却件数を基準にする
      // （APIがhasMore:trueのままlimit未満を返す可能性があっても取りこぼさないため）
      setExternalPagination({ offset: offset + videos.length, hasMore, pausedByError: false });
    } catch (e) {
      if (externalRequestIdRef.current !== requestId) return;
      console.error('外部動画取得エラー:', e);
      setExternalError(true);
      if (offset === 0) {
        setExternalVideos([]);
      }
      // offsetを分岐させず常にpausedByErrorを立てて一時停止する。
      // offset===0の場合だけ立てないと、hasMoreが初期値trueのまま残り続け、
      // 自動ロードeffectが「失敗→再取得→失敗」を無限に繰り返してしまう
      // （実測で500msあたり5000回超のリクエストが発生することを確認済み）。
      // hasMoreをfalseにしないのは、「データが尽きた」と区別し、
      // ネットワーク回復後も残りの外部動画を取得できるようにするため。
      setExternalPagination(prev => ({ ...prev, pausedByError: true }));
    } finally {
      // 自分がまだ最新のリクエストである場合のみロックを解放する。
      // 古い（破棄済みの）リクエストが後から解決してロックを解放すると、
      // 新しいリクエストが進行中なのに別のリクエストが発火できてしまう
      if (externalRequestIdRef.current === requestId) {
        isExternalLoadingRef.current = false;
      }
    }
  }, []);

  // 外部動画の取得（「すべて」または「院外」タブ選択時のみ）。
  // 初回表示時、およびsortOrderが変わった場合に、offset=0からやり直す
  // （APIがsortOrderに応じた順序で返すため、既存ページを引き継げない）
  const lastFetchedSortOrderRef = useRef<SortOrder | null>(null);
  useEffect(() => {
    if (videoSource === 'internal') return;
    if (lastFetchedSortOrderRef.current === filterSortOrder) return;
    lastFetchedSortOrderRef.current = filterSortOrder;
    // sortOrder変更はユーザー操作起点の明確な意図のため、ページング継続中の
    // 古いリクエストのロックを待たず、新しいリクエストを優先して発行する
    isExternalLoadingRef.current = false;
    setExternalVideos([]);
    setExternalPagination({ offset: 0, hasMore: true, pausedByError: false });
    loadExternalPage(0, filterSortOrder);
  }, [videoSource, filterSortOrder, loadExternalPage]);

  // タブ切り替えを「明示的な再試行」の合図として扱い、一時停止を解除する
  // （自動リトライはスクロールのたびに送らないが、ユーザー操作を起点に再試行できるようにする）
  useEffect(() => {
    if (videoSource === 'internal') return;
    setExternalPagination(prev => (prev.pausedByError ? { ...prev, pausedByError: false } : prev));
  }, [videoSource]);

  // 診療科フィルタの候補を取り直す。取れなくてもフィルタが空になるだけで一覧は出す
  const loadSpecialtyCounts = useCallback(async () => {
    try {
      const result = await fetchSpecialtyCounts(true);
      const entries: SpecialtyCount[] = result.specialties.filter(entry => entry.count > 0);
      if (result.unclassified_count > 0) {
        entries.push({ id: UNCLASSIFIED_SPECIALTY_KEY, name: UNCLASSIFIED_SPECIALTY_LABEL, count: result.unclassified_count });
      }
      setSpecialtyCounts({ status: 'ready', entries });
    } catch (error) {
      console.error('[useVideoList] 診療科フィルタ候補の取得に失敗:', error);
      // 候補が無いのに絞り込みだけ効いた状態にしない。ただし記憶は消さず、次回の復元に任せる
      setSpecialtyCounts({ status: 'error', entries: [] });
      setSelectedSpecialtyId('');
    }
  }, []);

  useEffect(() => {
    loadSpecialtyCounts();
  }, [loadSpecialtyCounts]);

  // 記憶していた診療科がもう候補に無い（動画が0本になった等）なら選択と記憶を外す。
  // 取得に成功したときだけ判定する（失敗を「候補に無い」と誤認しないため）
  useEffect(() => {
    if (specialtyCounts.status !== 'ready' || !selectedSpecialtyId) return;
    if (!specialtyCounts.entries.some(entry => entry.id === selectedSpecialtyId)) {
      setSelectedSpecialtyId('');
      saveSpecialtyFilter('');
    }
  }, [specialtyCounts, selectedSpecialtyId]);

  // 候補の先頭に「すべての診療科」を置き、選択を外す操作をソートのリセットと切り離す
  const specialtyOptions = useMemo(
    () => (specialtyCounts.entries.length > 0
      ? [ALL_SPECIALTIES_LABEL, ...specialtyCounts.entries.map(entry => entry.name)]
      : []),
    [specialtyCounts]
  );

  // FilterBar は診療科名で選ばせるので、名前と ID を相互に変換する
  const specialty = useMemo(
    () => specialtyCounts.entries.find(entry => entry.id === selectedSpecialtyId)?.name ?? '',
    [specialtyCounts, selectedSpecialtyId]
  );

  const setSpecialty = useCallback((name: string) => {
    // 「すべての診療科」や候補に無い名前は選択解除として扱う
    const nextId = specialtyCounts.entries.find(entry => entry.name === name)?.id ?? '';
    setSelectedSpecialtyId(nextId);
    saveSpecialtyFilter(nextId);
  }, [specialtyCounts]);

  // タブフィルタに応じた表示リストを計算
  // 検索クエリがある場合は外部動画を除外（外部動画は検索APIの対象外のため）
  // 診療科で絞っている間も外部動画は出さない（外部動画は診療科の情報を持たない）
  const videoData = useMemo(() => {
    if (query.trim()) {
      return internalVideos;
    }
    const externalCandidates = selectedSpecialtyId ? [] : externalVideos;
    const sortByDate = (videos: Video[]) =>
      [...videos].sort((a, b) => {
        const diff = getVideoSortTime(b) - getVideoSortTime(a);
        return sortOrder === 'ASC' ? -diff : diff;
      });
    switch (videoSource) {
      case 'internal':
        return internalVideos;
      case 'external':
        return sortByDate(externalCandidates);
      case 'all':
      default:
        // 自院動画・外部動画を日時順に混ぜて表示する（外部動画を末尾に固定しない）
        return sortByDate([...internalVideos, ...externalCandidates]);
    }
  }, [videoSource, internalVideos, externalVideos, query, sortOrder, selectedSpecialtyId]);

  // 検索クエリ・ソート順・診療科フィルタが変更されたら、ページをリセットしてデータを取得
  useEffect(() => {
    setCurrentPage(1);
    loadData(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, filterSortOrder, selectedSpecialtyId]);

  // データ取得関数（ページ番号に応じて取得し、1ページ目なら置き換え、2ページ目以降なら追記する）
  const loadData = useCallback(async (page: number, searchWord: string | null = null) => {
    // 追記（2ページ目以降）の多重発火だけを防ぐ。1ページ目の取り直しは条件変更（検索・ソート・
    // 診療科フィルタ）が起点なので、進行中の取得があっても新しい方を優先する
    if (isLoadingRef.current && page > 1) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    const requestId = ++listRequestIdRef.current;
    try {
      let data;
      // searchWordが渡された場合はそれを優先して使用、そうでない場合はURLクエリパラメータを使用
      const searchQuery = searchWord !== null ? searchWord : query.trim();

      if (searchQuery) {
        // 検索クエリがある場合：検索 API を利用（ページネーション対応）。診療科フィルタは選択中だけ渡す
        data = selectedSpecialtyId
          ? await searchVideos(searchQuery, page, limit, sortBy, sortOrder, true, selectedSpecialtyId)
          : await searchVideos(searchQuery, page, limit, sortBy, sortOrder, true);
      } else {
        // 検索クエリがない場合：全件取得 API を利用
        data = selectedSpecialtyId
          ? await fetchPaginatedVideos(page, limit, sortBy, sortOrder, true, selectedSpecialtyId)
          : await fetchPaginatedVideos(page, limit, sortBy, sortOrder, true);
      }

      // 待機中により新しい取得が始まっていたら、この応答は古いので捨てる
      if (listRequestIdRef.current !== requestId) return;

      // データが空でないことを確認
      if (!data || !Array.isArray(data.videos)) {
        // API応答データが不正な形式の場合はデフォルト値を使用
        data = { videos: [], pagination: { currentPage: page, totalPages: 0, totalItems: 0 } };
      }

      // 検索結果が0件の場合は早期リターン
      if (data.videos.length === 0) {
        setInternalVideos([]);
        setPagination({
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages || 0,
        });
        setCurrentPage(page);
        setIsLoading(false);
        return;
      }

      // データの型変換を行う
      let convertedVideos: Video[] = data.videos.map(mapApiVideoToVideo);

      // 病院検索の場合は、病院情報のある動画のみに絞り込む
      if (searchQuery.startsWith('病院:')) {
        convertedVideos = convertedVideos.filter((video) =>
          video?.uploader?.hospital?.name &&
          video.uploader.hospital.name !== FALLBACK_LABELS.NO_AFFILIATION
        );

        // フィルタリング後に0件になった場合は早期リターン
        if (convertedVideos.length === 0) {
          setInternalVideos([]);
          setPagination({
            currentPage: 1,
            totalPages: 0,
          });
          setCurrentPage(page);
          setIsLoading(false);
          return;
        }
      }

      // 1ページ目の場合はリストを置き換え、2ページ目以降は追記する
      if (page === 1) {
        setInternalVideos(convertedVideos);
      } else {
        setInternalVideos(prevVideos => {
          // すでに存在する動画を除外して新たな動画だけを追加する
          const newVideos = convertedVideos.filter((video) =>
            !prevVideos.some(prevVideo => prevVideo.id === video.id)
          );
          return [...prevVideos, ...newVideos];
        });
      }
      setPagination({
        currentPage: data.pagination.currentPage,
        totalPages: data.pagination.totalPages,
      });
      setCurrentPage(page);
    } catch (error) {
      if (listRequestIdRef.current !== requestId) return;
      console.error('[useVideoList] 動画一覧の取得に失敗:', error);
      setInternalError(true);
      // エラー時は空の配列をセット（1ページ目の場合のみ）
      if (page === 1) {
        setInternalVideos([]);
        setPagination({
          currentPage: 1,
          totalPages: 0,
        });
      }
    } finally {
      // 自分がまだ最新の取得である場合だけロックを解放する（古い取得が新しい取得のロックを外さない）
      if (listRequestIdRef.current === requestId) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [query, limit, sortBy, sortOrder, selectedSpecialtyId]);

  // 自院動画・外部動画のうち、現在のタブでまだ読み込めるものがあるかどうか
  // （pausedByError中はスクロールのたびに再送しない。タブ切替で再試行される）
  // 検索中は外部動画自体が非表示（videoDataから除外）になるため、
  // 表示されない外部動画を裏で取得し続けないようクエリがある間は対象外にする
  // 診療科で絞っている間も外部動画は非表示なので、裏で取得し続けない
  const hasMoreInternal = currentPage < pagination.totalPages;
  const canLoadMoreExternal =
    !query.trim() && !selectedSpecialtyId && externalPagination.hasMore && !externalPagination.pausedByError;
  const hasMoreToLoad =
    (videoSource !== 'external' && hasMoreInternal) ||
    (videoSource !== 'internal' && canLoadMoreExternal);

  // 次のページを読み込む（「すべて」タブでは日時順マージを保つため両方のソースを同時に進める）
  const loadMore = useCallback(() => {
    if (videoSource !== 'external' && currentPage < pagination.totalPages) {
      loadData(currentPage + 1);
    }
    if (videoSource !== 'internal' && canLoadMoreExternal) {
      loadExternalPage(externalPagination.offset, filterSortOrder);
    }
  }, [videoSource, currentPage, pagination.totalPages, loadData, canLoadMoreExternal, externalPagination.offset, filterSortOrder, loadExternalPage]);

  // 無限スクロール：画面下部に到達したら次のページのデータを取得する
  useEffect(() => {
    const handleScroll = () => {
      // PC版とSP版で異なる閾値を設定
      const scrollThreshold = isMobile ? 150 : 500;

      // 現在のスクロール位置とページ全体の高さを確認
      const scrolledToBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - scrollThreshold;

      if (scrolledToBottom && !isLoading && hasMoreToLoad) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pagination.totalPages, isLoading, query, isMobile, hasMoreToLoad, loadMore]);

  // **自動ロード処理**
  // コンテンツの高さがウィンドウより短い場合、自動的に次のページをロードする
  useEffect(() => {
    if (!isLoading && hasMoreToLoad) {
      if (document.documentElement.scrollHeight <= window.innerHeight) {
        loadMore();
      }
    }
  }, [videoData, isLoading, hasMoreToLoad, loadMore]); // 必要な依存関係を追加

  // 検索タグクリック時の処理を追加
  const handleSearchTagClick = (searchWord: string) => {
    setSearchParams({ q: searchWord });
    setCurrentPage(1);
    setInternalVideos([]);
    loadData(1, searchWord);
  };

  // 公開/非公開状態が変更された時の処理
  const handleVisibilityChange = useCallback((videoId: string | number, newVisibility: string) => {
    // データの中から該当する動画を見つけて状態を更新
    setInternalVideos(prevVideos =>
      prevVideos.map(video =>
        video.id === videoId
          ? { ...video, visibility: newVisibility as 'public' | 'private' | 'hospital_only' }
          : video
      )
    );
  }, []);

  return {
    videoData,
    isLoading,
    isMobile,
    videoSource,
    setVideoSource,
    internalError,
    externalError,
    handleSearchTagClick,
    handleVisibilityChange,
    sortOrder: filterSortOrder,
    setSortOrder: setFilterSortOrder,
    specialty,
    setSpecialty,
    specialtyOptions,
    procedure,
    setProcedure,
  };
};
